import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getImageRelationsByCategory, getImageRelationsByGroup, getImageRelationsByCollectionId } from '../../api/image_relations';
import { getCollectionById, getCollectionCategoriesGrouped } from '../../api/collections';

// Async thunks
export const fetchGalleryData = createAsyncThunk(
    'gallery/fetchData',
    async ({ collectionId, categoryId, groupId }, { rejectWithValue }) => {
        try {
            let postcards;
            if (categoryId) {
                postcards = await getImageRelationsByCategory(collectionId, categoryId, false);
            } else if (groupId) {
                postcards = await getImageRelationsByGroup(collectionId, groupId, false);
            } else {
                postcards = await getImageRelationsByCollectionId(collectionId);
            }
            return postcards;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchCollectionInfo = createAsyncThunk(
    'gallery/fetchCollectionInfo',
    async (collectionId, { rejectWithValue }) => {
        try {
            const [collection, categories] = await Promise.all([
                getCollectionById(collectionId),
                getCollectionCategoriesGrouped(collectionId)
            ]);
            return { collection, categories };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const initialState = {
    // Данные галереи
    postcards: [],
    collection: null,
    categories: [],
    
    // Фильтры и состояние
    selectedCategoryId: null,
    selectedGroupId: null,
    currentCollectionId: null,
    
    // Состояния загрузки
    loading: {
        postcards: false,
        collection: false
    },
    
    // Ошибки
    errors: {
        postcards: null,
        collection: null
    },
    
    // Пагинация
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        hasMore: true
    },
    
    // Сортировка
    sortBy: 'created_at',
    sortOrder: 'desc'
};

const gallerySlice = createSlice({
    name: 'gallery',
    initialState,
    reducers: {
        // Установка выбранной категории
        setSelectedCategory: (state, action) => {
            state.selectedCategoryId = action.payload;
            state.selectedGroupId = null;
            state.pagination.page = 1;
        },
        
        // Установка выбранной группы
        setSelectedGroup: (state, action) => {
            state.selectedGroupId = action.payload;
            state.selectedCategoryId = null;
            state.pagination.page = 1;
        },
        
        // Установка коллекции
        setCurrentCollection: (state, action) => {
            state.currentCollectionId = action.payload;
            state.selectedCategoryId = null;
            state.selectedGroupId = null;
            state.pagination.page = 1;
        },
        
        // Сброс фильтров
        resetFilters: (state) => {
            state.selectedCategoryId = null;
            state.selectedGroupId = null;
            state.pagination.page = 1;
        },
        
        // Установка страницы
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        
        // Установка сортировки
        setSorting: (state, action) => {
            const { sortBy, sortOrder } = action.payload;
            state.sortBy = sortBy;
            state.sortOrder = sortOrder;
            state.pagination.page = 1;
        },
        
        // Добавление новых постеров (для пагинации)
        addPostcards: (state, action) => {
            state.postcards = [...state.postcards, ...action.payload];
        },
        
        // Очистка данных
        clearGalleryData: (state) => {
            state.postcards = [];
            state.pagination.page = 1;
            state.pagination.total = 0;
            state.pagination.hasMore = true;
        },
        
        // Сброс ошибок
        clearErrors: (state) => {
            state.errors = {
                postcards: null,
                collection: null
            };
        }
    },
    extraReducers: (builder) => {
        // Gallery data
        builder
            .addCase(fetchGalleryData.pending, (state) => {
                state.loading.postcards = true;
                state.errors.postcards = null;
            })
            .addCase(fetchGalleryData.fulfilled, (state, action) => {
                state.loading.postcards = false;
                state.postcards = action.payload;
                state.pagination.total = action.payload.length;
                state.pagination.hasMore = action.payload.length >= state.pagination.limit;
            })
            .addCase(fetchGalleryData.rejected, (state, action) => {
                state.loading.postcards = false;
                state.errors.postcards = action.payload;
            });
        
        // Collection info
        builder
            .addCase(fetchCollectionInfo.pending, (state) => {
                state.loading.collection = true;
                state.errors.collection = null;
            })
            .addCase(fetchCollectionInfo.fulfilled, (state, action) => {
                state.loading.collection = false;
                state.collection = action.payload.collection;
                state.categories = action.payload.categories;
            })
            .addCase(fetchCollectionInfo.rejected, (state, action) => {
                state.loading.collection = false;
                state.errors.collection = action.payload;
            });
    }
});

export const {
    setSelectedCategory,
    setSelectedGroup,
    setCurrentCollection,
    resetFilters,
    setPage,
    setSorting,
    addPostcards,
    clearGalleryData,
    clearErrors
} = gallerySlice.actions;

// Selectors
export const selectGalleryPostcards = (state) => state.gallery.postcards;
export const selectGalleryCollection = (state) => state.gallery.collection;
export const selectGalleryCategories = (state) => state.gallery.categories;
export const selectSelectedCategory = (state) => state.gallery.selectedCategoryId;
export const selectSelectedGroup = (state) => state.gallery.selectedGroupId;
export const selectCurrentCollection = (state) => state.gallery.currentCollectionId;
export const selectGalleryLoading = (state) => state.gallery.loading;
export const selectGalleryErrors = (state) => state.gallery.errors;
export const selectGalleryPagination = (state) => state.gallery.pagination;
export const selectGallerySorting = (state) => ({
    sortBy: state.gallery.sortBy,
    sortOrder: state.gallery.sortOrder
});

export default gallerySlice.reducer;
